import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import Modal from "../../components/ui-components/Modal";
import { Dropdown } from "../../components/form-components/Dropdown";
import { MoneyInput, MoneyText } from "../../components/form-components/MoneyInput";
import ScopeNodeTreeSelect from "../../components/Budgets/ScopeNodeTreeSelect";
import { toast } from "../../components/ui-components/Toast";
import { useHasCapability } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";
import {
  useUserAuthorities,
  createUserAuthority,
  deleteUserAuthority,
  authoritiesKey,
  APPROVABLE_OBJECT_TYPES,
} from "./authorityApi";
import { listBudgetDimensions } from "./scopeApi";

// Sentinel for "no scope clause" in the scope dropdown — kept distinct from
// any real dimension id (which are positive ints) and from `null` (used
// internally for "not yet scoped").
const WHOLE_COMPANY = "whole_company";

const OBJECT_TYPE_LABELS = APPROVABLE_OBJECT_TYPES.reduce((m, o) => {
  m[o.value] = o.label;
  return m;
}, {});

function formatObjectType(val) {
  return OBJECT_TYPE_LABELS[val] || val || "—";
}

function formatDate(s) {
  return new Date(s).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatValidity(startsAt, endsAt) {
  if (!startsAt && !endsAt) return "No expiry";
  if (startsAt && endsAt) return `${formatDate(startsAt)} – ${formatDate(endsAt)}`;
  if (endsAt) return `Until ${formatDate(endsAt)}`;
  return `From ${formatDate(startsAt)}`;
}

// Maps backend error codes (see backend_go
// cmd/api/approval_authority_handlers.go) to messages a tenant admin can act
// on.
function friendlyAuthorityError(err, action) {
  const code = err?.serverBody?.error?.code;
  const serverMessage = err?.serverBody?.error?.message;
  switch (code) {
    case "INVALID_OBJECT_TYPE":
      return "Select a valid object type.";
    case "INVALID_AMOUNT":
      return "Enter an amount limit greater than zero.";
    case "INVALID_DATE":
      return "Enter a valid effective-from or expiry date.";
    case "INVALID_DIMENSION":
      return "The selected scope dimension isn't recognized. Refresh and try again.";
    case "INVALID_OPERATOR":
      return "That scope condition isn't supported.";
    case "INVALID_CLAUSE":
      return "Select a value for the scope condition.";
    case "USER_NOT_FOUND":
      return "This user no longer exists.";
    case "AUTHORITY_NOT_FOUND":
      return "This approval authority no longer exists — it may already have been removed.";
    default:
      return serverMessage || err?.message || `Failed to ${action} approval authority`;
  }
}

const emptyDraft = () => ({
  objectType: null,
  maxAmount: null,
  startsAt: "",
  endsAt: "",
  dimensionId: null,
  nodeId: null,
  nodeLabel: null,
});

/**
 * ApprovalAuthorities — "Approval authority" panel on the user detail page.
 * Lists the user's explicit approval-authority grants (object type + amount
 * limit + validity window + org scope) and, for viewers holding
 * config.budget_policies, lets them add a new grant or remove one.
 */
const ApprovalAuthorities = ({ userId }) => {
  const canManage = useHasCapability(CAPS.CONFIG_BUDGET_POLICIES);

  const queryClient = useQueryClient();
  const authoritiesQ = useUserAuthorities(userId);
  const dimsQ = useQuery({
    queryKey: ["budget-dimensions"],
    queryFn: listBudgetDimensions,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const invalidateAuthorities = () =>
    queryClient.invalidateQueries({ queryKey: authoritiesKey(userId) });

  const createMut = useMutation({
    mutationFn: (body) => createUserAuthority(userId, body),
    onSuccess: invalidateAuthorities,
  });
  const deleteMut = useMutation({
    mutationFn: (authId) => deleteUserAuthority(userId, authId),
    onSuccess: invalidateAuthorities,
  });

  const authorities = authoritiesQ.data || [];
  const dimensions = useMemo(() => dimsQ.data || [], [dimsQ.data]);

  const dimById = useMemo(() => {
    const m = {};
    for (const d of dimensions) m[d.id] = d;
    return m;
  }, [dimensions]);

  const scopeOptions = useMemo(
    () => [
      { value: WHOLE_COMPANY, label: "Whole company (no restriction)" },
      ...dimensions.map((d) => ({ value: d.id, label: d.name })),
    ],
    [dimensions]
  );

  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [removeCandidate, setRemoveCandidate] = useState(null);

  const openAdd = () => {
    setDraft(emptyDraft());
    setShowAdd(true);
  };
  const closeAdd = () => setShowAdd(false);

  const handleObjectTypeChange = (opt) =>
    setDraft((d) => ({ ...d, objectType: opt?.value ?? null }));

  const handleScopeChange = (opt) => {
    if (!opt || opt.value === WHOLE_COMPANY) {
      setDraft((d) => ({ ...d, dimensionId: null, nodeId: null, nodeLabel: null }));
    } else {
      setDraft((d) => ({ ...d, dimensionId: opt.value, nodeId: null, nodeLabel: null }));
    }
  };

  const handleNodeChange = (opt) =>
    setDraft((d) => ({ ...d, nodeId: opt?.value ?? null, nodeLabel: opt?.label ?? null }));

  const isDraftComplete =
    draft.objectType != null &&
    draft.maxAmount != null &&
    draft.maxAmount > 0 &&
    (draft.dimensionId == null || draft.nodeId != null);

  const handleSave = () => {
    if (!isDraftComplete) {
      toast.error("Select an object type, an amount limit, and (if scoping) a value.");
      return;
    }
    const body = {
      objectType: draft.objectType,
      maxAmount: draft.maxAmount,
      clauses:
        draft.dimensionId != null
          ? [
              {
                dimensionCode: dimById[draft.dimensionId]?.code,
                operator: "in_subtree",
                nodeId: draft.nodeId,
              },
            ]
          : [],
    };
    if (draft.startsAt) body.startsAt = new Date(draft.startsAt).toISOString();
    if (draft.endsAt) body.endsAt = new Date(draft.endsAt).toISOString();

    createMut.mutate(body, {
      onSuccess: () => {
        toast.success("Approval authority added");
        setShowAdd(false);
      },
      onError: (err) => toast.error(friendlyAuthorityError(err, "add")),
    });
  };

  const handleRemoveConfirm = () => {
    if (!removeCandidate) return;
    deleteMut.mutate(removeCandidate.id, {
      onSuccess: () => {
        toast.success("Approval authority removed");
        setRemoveCandidate(null);
      },
      onError: (err) => {
        setRemoveCandidate(null);
        toast.error(friendlyAuthorityError(err, "remove"));
      },
    });
  };

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
      <div className="border-b border-border pb-3 mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text">Approval authority</h3>
          <p className="text-xs text-text-2 mt-0.5">
            Grants this user explicit authority to approve governed objects, optionally
            capped by amount, validity window, or org scope.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={openAdd}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-ctrl text-sm font-medium text-primary border border-solid border-primary hover:bg-primary-soft transition-colors cursor-pointer box-border"
          >
            <FontAwesomeIcon
              icon={icon({ name: "plus", style: "solid" })}
              className="text-xs"
            />
            Add authority
          </button>
        )}
      </div>

      {authoritiesQ.isLoading && (
        <div className="text-sm text-text-2">Loading approval authorities…</div>
      )}

      {authoritiesQ.isError && (
        <div className="text-sm text-danger">Failed to load approval authorities.</div>
      )}

      {!authoritiesQ.isLoading && !authoritiesQ.isError && authorities.length === 0 && (
        <div className="text-sm text-text-2 italic">
          No approval authority grants. This user can only approve via role-based policy
          tiers.
        </div>
      )}

      {!authoritiesQ.isLoading && authorities.length > 0 && (
        <ul className="space-y-3 list-none m-0 p-0">
          {authorities.map((a) => (
            <li
              key={a.id}
              className="rounded-lg border border-border bg-surface-2/50 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-text">
                      {formatObjectType(a.objectType)}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        a.isEnabled
                          ? "bg-success-soft text-success"
                          : "bg-surface-2 text-text-2"
                      }`}
                    >
                      {a.isEnabled ? "Active" : "Disabled"}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-text-2">
                    Up to{" "}
                    <MoneyText
                      value={
                        a.maxAmount ?? (a.maxAmountCents != null ? a.maxAmountCents / 100 : null)
                      }
                    />{" "}
                    &middot; {formatValidity(a.startsAt, a.endsAt)}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {a.clauses && a.clauses.length > 0 ? (
                      a.clauses.map((clause, idx) => (
                        <span
                          key={idx}
                          data-testid="authority-scope-chip"
                          className="inline-flex items-center gap-1 text-xs bg-card border border-border text-text px-2 py-1 rounded-ctrl"
                        >
                          {clause.nodeLabel || clause.nodeId}
                        </span>
                      ))
                    ) : (
                      <span
                        data-testid="authority-scope-chip"
                        className="inline-flex items-center gap-1 text-xs bg-card border border-border text-text-2 px-2 py-1 rounded-ctrl"
                      >
                        Whole company
                      </span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setRemoveCandidate(a)}
                    className="shrink-0 p-1.5 text-text-2 hover:text-danger transition-colors cursor-pointer border-0 bg-transparent box-border"
                    title="Remove approval authority"
                    aria-label={`Remove approval authority for ${formatObjectType(a.objectType)}`}
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: "trash", style: "solid" })}
                      className="text-xs"
                    />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add authority */}
      <Modal
        show={showAdd}
        close={closeAdd}
        title="Add approval authority"
        description="Grant explicit authority to approve a governed object type, optionally capped by amount, validity window, or org scope. Leave scope on 'Whole company' for an unscoped grant."
        confirmButton="Add authority"
        cancelButton="Cancel"
        confirmAction={handleSave}
        size="lg"
      >
        <div className="space-y-4" style={{ minWidth: "420px" }}>
          <div>
            <label className="block mb-1 text-sm font-regular text-text-2">Object type</label>
            <Dropdown
              aria-label="Object type"
              options={APPROVABLE_OBJECT_TYPES}
              selectedValue={
                draft.objectType != null
                  ? APPROVABLE_OBJECT_TYPES.find((o) => o.value === draft.objectType)
                  : null
              }
              setSelectedValue={handleObjectTypeChange}
              fullWidth
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            />
          </div>

          <div>
            <label htmlFor="authorityMaxAmount" className="block mb-1 text-sm font-regular text-text-2">
              Amount limit
            </label>
            <MoneyInput
              id="authorityMaxAmount"
              name="authorityMaxAmount"
              value={draft.maxAmount}
              onChange={(n) => setDraft((d) => ({ ...d, maxAmount: n }))}
              allowNegative={false}
              min={0}
              decimals={2}
              placeholder="0.00"
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <label
                htmlFor="authorityStartsAt"
                className="block mb-1 text-sm font-regular text-text-2"
              >
                Effective from (optional)
              </label>
              <input
                id="authorityStartsAt"
                type="date"
                aria-label="Effective from"
                className="w-full border border-solid border-border rounded-ctrl px-3 py-2 text-sm h-11 box-border"
                value={draft.startsAt}
                onChange={(e) => setDraft((d) => ({ ...d, startsAt: e.target.value }))}
              />
            </div>
            <div className="flex-1 min-w-0">
              <label
                htmlFor="authorityEndsAt"
                className="block mb-1 text-sm font-regular text-text-2"
              >
                Expires (optional)
              </label>
              <input
                id="authorityEndsAt"
                type="date"
                aria-label="Expires"
                className="w-full border border-solid border-border rounded-ctrl px-3 py-2 text-sm h-11 box-border"
                value={draft.endsAt}
                onChange={(e) => setDraft((d) => ({ ...d, endsAt: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 text-sm font-regular text-text-2">
              Scope (optional — restricts approval to a subtree)
            </label>
            <div className="flex items-start gap-2">
              <div style={{ minWidth: "200px" }}>
                <Dropdown
                  aria-label="Scope"
                  options={scopeOptions}
                  selectedValue={
                    draft.dimensionId != null
                      ? { value: draft.dimensionId, label: dimById[draft.dimensionId]?.name || "" }
                      : scopeOptions[0]
                  }
                  setSelectedValue={handleScopeChange}
                  fullWidth
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                />
              </div>
              {draft.dimensionId != null && (
                <div className="flex-1 min-w-0">
                  <ScopeNodeTreeSelect
                    dimensionId={draft.dimensionId}
                    value={draft.nodeId}
                    label={draft.nodeLabel}
                    onChange={handleNodeChange}
                    placeholder="Select a value"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Remove confirm */}
      <Modal
        show={!!removeCandidate}
        close={() => setRemoveCandidate(null)}
        title="Remove this approval authority?"
        description={
          removeCandidate
            ? `This user will no longer have explicit authority to approve ${formatObjectType(
                removeCandidate.objectType
              )}.`
            : ""
        }
        confirmButton="Remove"
        cancelButton="Cancel"
        alertIcon
        confirmAction={handleRemoveConfirm}
      />
    </div>
  );
};

ApprovalAuthorities.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default ApprovalAuthorities;
